import { logger } from "@/lib/logger";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface SiteInvoice {
  id: string;
  invoice_number: string;
  trial_site_id: string;
  sponsor_organization_id: string;
  trial_id: string;
  billing_period_start: string;
  billing_period_end: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  currency: string;
  status: 'draft' | 'sent' | 'paid' | 'cancelled';
  sent_at?: string;
  paid_at?: string;
  due_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Joined data
  trial_site?: {
    name: string;
  };
  sponsor?: {
    name: string;
  };
  trial?: {
    trial_title: string;
  };
}

export function useSiteInvoices(filters?: {
  trialSiteId?: string;
  sponsorOrgId?: string;
  status?: 'draft' | 'sent' | 'paid' | 'cancelled';
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['site-invoices', filters],
    queryFn: async () => {
      // Step 1: Fetch base invoice data without complex joins that may fail
      let baseQuery = supabase
        .from('site_invoices')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.trialSiteId) {
        baseQuery = baseQuery.eq('trial_site_id', filters.trialSiteId);
      }

      if (filters?.sponsorOrgId) {
        baseQuery = baseQuery.eq('sponsor_organization_id', filters.sponsorOrgId);
      }

      if (filters?.status) {
        baseQuery = baseQuery.eq('status', filters.status);
      }

      const { data: invoices, error } = await baseQuery;
      
      if (error) {
        logger.error('Error fetching invoices:', error);
        throw error;
      }

      if (!invoices || invoices.length === 0) {
        return { invoices: [] as SiteInvoice[], count: 0 };
      }

      // Step 2: Fetch related data in parallel for better performance
      const siteIds = [...new Set(invoices.map(i => i.trial_site_id).filter(Boolean))];
      const sponsorIds = [...new Set(invoices.map(i => i.sponsor_organization_id).filter(Boolean))];
      const trialIds = [...new Set(invoices.map(i => i.trial_id).filter(Boolean))];

      const [sitesResult, sponsorsResult, trialsResult] = await Promise.all([
        // Fetch trial sites - use 'name' column (not 'site_name')
        siteIds.length > 0 
          ? supabase.from('trial_sites_safe').select('id, name').in('id', siteIds)
          : Promise.resolve({ data: [], error: null }),
        // Fetch sponsor organizations
        sponsorIds.length > 0
          ? supabase.from('organizations').select('id, name').in('id', sponsorIds)
          : Promise.resolve({ data: [], error: null }),
        // Fetch trials from cro_trials (the correct table)
        trialIds.length > 0
          ? supabase.from('cro_trials').select('id, trial_title').in('id', trialIds)
          : Promise.resolve({ data: [], error: null })
      ]);

      // Build lookup maps
      const sitesMap = new Map((sitesResult.data || []).map(s => [s.id, s]));
      const sponsorsMap = new Map((sponsorsResult.data || []).map(s => [s.id, s]));
      const trialsMap = new Map((trialsResult.data || []).map(t => [t.id, t]));

      // Merge data with proper type casting
      const enrichedInvoices: SiteInvoice[] = invoices.map(invoice => ({
        id: invoice.id,
        invoice_number: invoice.invoice_number,
        trial_site_id: invoice.trial_site_id,
        sponsor_organization_id: invoice.sponsor_organization_id,
        trial_id: invoice.trial_id,
        billing_period_start: invoice.billing_period_start,
        billing_period_end: invoice.billing_period_end,
        subtotal: Number(invoice.subtotal) || 0,
        tax_amount: Number(invoice.tax_amount) || 0,
        total_amount: Number(invoice.total_amount) || 0,
        currency: invoice.currency || 'USD',
        status: (invoice.status as 'draft' | 'sent' | 'paid' | 'cancelled') || 'draft',
        sent_at: invoice.sent_at || undefined,
        paid_at: invoice.paid_at || undefined,
        due_date: invoice.due_date || undefined,
        notes: invoice.notes || undefined,
        created_at: invoice.created_at,
        updated_at: invoice.updated_at,
        trial_site: sitesMap.get(invoice.trial_site_id) 
          ? { name: sitesMap.get(invoice.trial_site_id)!.name }
          : undefined,
        sponsor: sponsorsMap.get(invoice.sponsor_organization_id)
          ? { name: sponsorsMap.get(invoice.sponsor_organization_id)!.name }
          : undefined,
        trial: trialsMap.get(invoice.trial_id)
          ? { trial_title: trialsMap.get(invoice.trial_id)!.trial_title }
          : undefined,
      }));

      return { invoices: enrichedInvoices, count: enrichedInvoices.length };
    },
    enabled: true,
    staleTime: 60 * 1000, // 1 minute
    retry: 1, // Limit retries to prevent long stalls
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'draft' | 'sent' | 'paid' | 'cancelled' }) => {
      const updates: Record<string, unknown> = { status };
      
      if (status === 'sent') {
        updates.sent_at = new Date().toISOString();
      } else if (status === 'paid') {
        updates.paid_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('site_invoices')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-invoices'] });
      toast({
        title: "Success",
        description: "Invoice status updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update invoice status",
        variant: "destructive",
      });
    },
  });

  return {
    ...query,
    updateInvoiceStatus: updateStatusMutation.mutate,
    isUpdating: updateStatusMutation.isPending,
  };
}
